import { useEffect, useState } from 'react'
import { Button, Input, Select, Tag } from 'antd'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { useFeedback } from '@/utils/antdApp'
import AIManagers from '../components/AIManagers'
import { useParams } from 'react-router-dom'
import { createManager, getManagersDetail, getStrategyVersions, updateManager } from '@/api'
import type { Manager, ManagerDetail, StrategyVersion } from '@/types'
import { sectorOptions } from '@/pages/static'
import ChatLayout from '@/Layout/ChatLayout'
import './index.scss'

const riskOptions = [
    { label: '低风险', value: '低风险' },
    { label: '中等', value: '中等' },
    { label: '中高风险', value: '中高风险' },
    { label: '高风险', value: '高风险' },
]

const createInitialFormData = (): Manager => ({
    nickname: '',
    initials: '',
    style: '',
    risk_level: '中等',
    investment_brief: '',
    strategy: '',
    focus_sectors: [],
    constraints: [],
})

const options = [
    { label: '成长价值', value: '成长价值' },
    { label: '量化动量', value: '量化动量' },
    { label: '稳定成长', value: '稳定成长' },
    { label: '价值投资', value: '价值投资' },
]

export default function OperateManagerPage() {
    const { message } = useFeedback()
    const navigate = useNavigate()
    const [title, setTitle] = useState('创建组合经理')
    const { id } = useParams<{ id: string }>()
    const [formData, setFormData] = useState<Manager>(createInitialFormData)
    const [saving, setSaving] = useState(false)
    const [versionsLoading, setVersionsLoading] = useState(false)
    const [editableVersions, setEditableVersions] = useState<StrategyVersion[]>([])
    const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null)

    const updateFormData = <K extends keyof Manager>(key: K, value: Manager[K]) => {
        setFormData((prev) => ({ ...prev, [key]: value }))
    }

    const toggleSector = (sector: string, checked: boolean) => {
        setFormData((prev) => ({
            ...prev,
            focus_sectors: checked
                ? [...prev.focus_sectors, sector]
                : prev.focus_sectors.filter((item) => item !== sector),
        }))
    }

    const sortEditableVersions = (list: StrategyVersion[]) => {
        return [...list].sort((a, b) => {
            if (a.status === 'active' && b.status !== 'active') return -1
            if (b.status === 'active' && a.status !== 'active') return 1
            const at = Date.parse(String(a.activated_at || a.archived_at || a.created_at || 0))
            const bt = Date.parse(String(b.activated_at || b.archived_at || b.created_at || 0))
            return bt - at
        })
    }

    const strategyFieldsFromVersion = (version: StrategyVersion | undefined, fallback: ManagerDetail) => ({
        investment_brief: version?.investment_brief ?? fallback.investment_brief ?? '',
        strategy: version ? (version.strategy || version.strategy_snapshot || '') : (fallback.strategy ?? ''),
        focus_sectors: version?.focus_sectors ?? fallback.focus_sectors ?? [],
        constraints: version?.constraints ?? fallback.constraints ?? [],
    })

    const load = () => {
        setVersionsLoading(true)
        getManagersDetail(Number(id))
            .then((data: ManagerDetail) => {
                setTitle(`编辑 · ${data.style}`)
                setFormData({
                    id: data.id,
                    nickname: data.nickname,
                    initials: data.initials,
                    style: data.style,
                    risk_level: data.risk_level,
                    investment_brief: data.investment_brief,
                    strategy: data.strategy,
                    focus_sectors: data.focus_sectors,
                    constraints: data.constraints,
                })
                return getStrategyVersions(Number(id), ['active', 'archived'])
                    .then((versionRes) => {
                        const sorted = sortEditableVersions(versionRes.versions ?? [])
                        const selected = sorted.find((item) => item.status === 'active') ?? sorted[0]
                        setEditableVersions(sorted)
                        setSelectedVersionId(selected?.id ?? null)
                        if (selected) {
                            setFormData((prev) => ({
                                ...prev,
                                ...strategyFieldsFromVersion(selected, data),
                            }))
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                        message.warning('策略版本列表加载失败，保存时将修改当前激活版本')
                    })
            })
            .catch((err) => {
                console.error(err)
                message.error('加载基金经理失败')
            })
            .finally(() => {
                setVersionsLoading(false)
            })
    }

    const handleSelectVersion = (versionId: number) => {
        setSelectedVersionId(versionId)
        const version = editableVersions.find((item) => item.id === versionId)
        if (!version) return
        setFormData((prev) => ({
            ...prev,
            investment_brief: version.investment_brief ?? '',
            strategy: version.strategy || version.strategy_snapshot || '',
            focus_sectors: version.focus_sectors ?? [],
            constraints: version.constraints ?? [],
        }))
    }

    useEffect(() => {
        if (id) {
            load()
        } else {
            setTitle('创建组合经理')
            setFormData(createInitialFormData())
            setEditableVersions([])
            setSelectedVersionId(null)
        }
    }, [id])

    const addConstraint = () => {
        setFormData((prev) => ({
            ...prev,
            constraints: [...prev.constraints, ''],
        }))
    }

    const updateConstraintDraft = (value: string, index: number) => {
        setFormData((prev) => ({
            ...prev,
            constraints: prev.constraints.map((item, itemIndex) => itemIndex === index ? value : item),
        }))
    }

    const changeConstraint = (value: string , index: number) => {
        const val = value.trim()
        setFormData((prev) => ({
            ...prev,
            constraints: (() => {
                if (!val) {
                    return prev.constraints.filter((_, itemIndex) => itemIndex !== index)
                }

                if (prev.constraints.some((item, itemIndex) => itemIndex !== index && item.trim() === val)) {
                    message.warning('该约束已存在')
                    return prev.constraints.map((item, itemIndex) => itemIndex === index ? '' : item)
                }

                return prev.constraints.map((item, itemIndex) => itemIndex === index ? val : item)
            })(),
        }))
    }

    const removeConstraint = (index: number) => {
        setFormData((prev) => ({
            ...prev,
            constraints: prev.constraints.filter((_, itemIndex) => itemIndex !== index),
        }))
        console.log(formData)
    }

    const handleSubmit = async () => {
        if (!formData.nickname.trim()) {
            message.warning('请输入经理昵称')
            return
        }

        if (!formData.investment_brief.trim()) {
            message.warning('请填写投资逻辑')
            return
        }

        if (!formData.strategy.trim()) {
            message.warning('请填写详细描述')
            return
        }

        setSaving(true)
        try {
            if(id){
                await updateManager({
                    ...formData,
                    ...(selectedVersionId != null ? { strategy_version_id: selectedVersionId } : {}),
                })
            }else{
                await createManager(formData)
            }
            message.success(id
                ? `组合经理「${formData.nickname.trim()}」已保存`
                : `组合经理「${formData.nickname.trim()}」已创建`)
            navigate('/managers')
        } catch (error) {
            message.error(error instanceof Error ? error.message : (id ? '保存失败' : '创建失败'))
        } finally {
            setSaving(false)
        }
    }

    const changeStyle = (value: string[]) => {
        updateFormData('style', value[value.length - 1] ?? '')
    }

    return (
            <ChatLayout
                className='operate-manager-page'
                headerClassName="operate-manager-page__header"
                contentClassName="operate-manager-page__form-column"
                siderClassName="operate-manager-page__ai-panel"
                header={
                    <>
                        <div className="flex flex-center">
                            <Button
                                type="text"
                                className="operate-manager-page__back"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate('/managers')}
                            >
                                全部经理
                            </Button>&nbsp;/
                            <div className="operate-manager-page__title">{title}</div>
                        </div>
                        <Button type="primary" onClick={handleSubmit} loading={saving}>
                            {id ? '✓ 保存修改' : '✓ 完成创建'}
                        </Button>
                    </>
                }
                content={
                    <>
                        <section className="operate-manager-page__section">
                            <div className="operate-manager-page__section-title">基本信息</div>
                            <div className="operate-manager-page__grid">
                                <div className="operate-manager-page__field">
                                    <label>经理昵称*</label>
                                    <Input
                                        value={formData.nickname}
                                        onChange={(e) => updateFormData('nickname', e.target.value)}
                                        placeholder="例如：稳健成长型、量化价值..."
                                    />
                                </div>

                                <div className="operate-manager-page__field">
                                    <label>投资风格</label>
                                    <Select
                                        mode="tags"
                                        value={formData.style ? [formData.style] : []}
                                        style={{ width: '100%' }}
                                        placeholder="例：价值成长、量化动量..."
                                        onChange={(value) => changeStyle(value)}
                                        options={options}
                                    />
                                    {/* <Input
                                        value={formData.style}
                                        onChange={(e) => updateFormData('style', e.target.value)}
                                        placeholder="例：价值成长、量化动量..."
                                    /> */}
                                </div>

                                <div className="operate-manager-page__field">
                                    <label>风险等级</label>
                                    <Select
                                        value={formData.risk_level}
                                        options={riskOptions}
                                        onChange={(value) => updateFormData('risk_level', value)}
                                    />
                                </div>

                                <div className="operate-manager-page__field">
                                    <label>缩写标识（2-3字）</label>
                                    <Input
                                        value={formData.initials}
                                        onChange={(e) => updateFormData('initials', e.target.value)}
                                        placeholder="例：SC、QM..."
                                    />
                                </div>
                            </div>
                        </section>

                        {id && (
                            <section className="operate-manager-page__section">
                                <div className="operate-manager-page__section-title">策略版本</div>
                                <div className="operate-manager-page__field">
                                    <label>选择要修改的版本</label>
                                    <Select
                                        value={selectedVersionId ?? undefined}
                                        loading={versionsLoading}
                                        placeholder="请选择已激活或已归档的策略版本"
                                        style={{ width: '100%' }}
                                        disabled={!editableVersions.length}
                                        onChange={handleSelectVersion}
                                        options={editableVersions.map((version) => ({
                                            value: version.id,
                                            label: `${version.version_no} · ${version.name}（${version.status === 'active' ? '已激活' : '已归档'}）`,
                                        }))}
                                    />
                                </div>
                                <div className="operate-manager-page__hint">
                                    {editableVersions.length
                                        ? '保存时写入所选版本。已激活和已归档版本都可以修改，保存后状态保持不变。'
                                        : '暂无已激活或已归档的策略版本。'}
                                </div>
                            </section>
                        )}

                        <section className="operate-manager-page__section">
                            <div className="operate-manager-page__section-title">策略描述</div>

                            <div className="operate-manager-page__field">
                                <label>投资策略</label>
                                <Input.TextArea
                                    value={formData.investment_brief}
                                    onChange={(e) => updateFormData('investment_brief', e.target.value)}
                                    placeholder="简要描述核心投资逻辑和选股思路..."
                                    rows={2}
                                />
                            </div>

                            <div className="operate-manager-page__field">
                                <label style={{ marginTop: '20px' }}>详细描述</label>
                                <Input.TextArea
                                    value={formData.strategy}
                                    onChange={(e) => updateFormData('strategy', e.target.value)}
                                    placeholder="详细说明投资理念、方法论和适用场景..."
                                    rows={12}
                                />
                            </div>
                        </section>

                        <section className="operate-manager-page__section">
                            <div className="operate-manager-page__section-title">关注板块</div>
                            <div className="operate-manager-page__tags">
                                {sectorOptions.map((sector) => (
                                    <Tag.CheckableTag
                                        key={sector}
                                        checked={formData.focus_sectors.includes(sector)? true : false}
                                        onChange={(checked) => toggleSector(sector, checked)}
                                        className="border-tag text-11"
                                    >
                                        {sector}
                                    </Tag.CheckableTag>
                                ))}
                            </div>
                        </section>

                        <section className="operate-manager-page__section">
                            <div className="operate-manager-page__section-title">风险约束</div>
                            <div className="operate-manager-page__constraints">
                                {formData.constraints.map((item, index) => (
                                    <div key={index} className="operate-manager-page__constraint-item flex flex-center">
                                        <span className="operate-manager-page__constraint-dot" />
                                        <Input
                                            value={item}
                                            onChange={(e) => updateConstraintDraft(e.target.value, index)}
                                            onBlur={(e) => changeConstraint(e.target.value, index)}
                                            placeholder="例如：单股持仓不超过20%、行业暴露不超过30%..."
                                        />
                                        <Button
                                            type="text"
                                            style={{ border: 'none' }}
                                            icon={<CloseOutlined />}
                                            onClick={() => removeConstraint(index)}
                                        />
                                    </div>
                                ))}
                            </div>
                            <Button icon={<PlusOutlined />} style={{ marginTop: '14px' }} onClick={addConstraint}>
                                添加约束条件
                            </Button>
                        </section>
                    </>
                }
                sider={
                    <AIManagers
                        type={id ? 'edit' : 'create'}
                        formData={formData}
                        updateFormData={(nextFormData) => setFormData({ ...formData, ...nextFormData })}
                    />
                }
        />
    )
}